import { UserRole } from "@fuse/recoil/pluginStudio/types"

import { ExamplePlugin } from "../types"

export const guestNotesPlugin: ExamplePlugin = {
  id: "guest-notes",
  name: "Guest Notes",
  description:
    "Allow guests to leave notes and messages for the event. Great for feedback or memories.",
  category: "Communication",
  tags: ["intermediate", "notes", "messages", "feedback"],
  difficulty: "intermediate",
  preview: "Guests can add, edit, and view notes in a shared space",
  iconName: "lightning",
  files: {
    [UserRole.GUEST]: `import React, { useState, useEffect } from 'react';

export default function GuestView({ pluginApi, eventId, userId, isLoggedIn }) {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [userName, setUserName] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const data = await pluginApi.getPluginEventInstanceData();
        setNotes(data?.notes || []);
      } catch (error) {
        console.error('Error loading notes:', error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const addNote = async () => {
    if (!newNote.trim() || !userName.trim()) return;

    const note = {
      id: Date.now(),
      text: newNote,
      author: userName,
      timestamp: new Date().toLocaleString(),
      userId: userId
    };

    const updatedNotes = [...notes, note];
    setNotes(updatedNotes);
    setNewNote('');
    
    try {
      await pluginApi.savePluginEventInstanceData({ notes: updatedNotes });
    } catch (error) {
      console.error('Error saving note:', error);
      // Revert on error
      setNotes(notes);
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-md max-w-2xl mx-auto">
        <div className="text-center">Loading notes...</div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow-md max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold mb-4">Guest Notes 📝</h2>
      
      {/* Add new note */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <input
          type="text"
          value={userName}
          onChange={(e) => setUserName(e.target.value)}
          placeholder="Your name"
          className="w-full px-3 py-2 mb-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <textarea
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          placeholder="Leave a note for the event..."
          className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          rows={3}
        />
        <button
          onClick={addNote}
          disabled={!newNote.trim() || !userName.trim()}
          className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 transition-colors"
        >
          Add Note
        </button>
      </div>

      {/* Display notes */}
      <div className="space-y-3">
        {notes.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No notes yet. Be the first to leave one!</p>
        ) : (
          notes.map((note) => (
            <div key={note.id} className="p-3 bg-blue-50 rounded-lg border-l-4 border-blue-500">
              <p className="text-gray-800 mb-2">{note.text}</p>
              <div className="text-sm text-gray-600">
                <span className="font-medium">{note.author}</span> • {note.timestamp}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}`,
    [UserRole.HOST_EVENT_VIEW]: `import React, { useState, useEffect } from 'react';

export default function HostView({ pluginApi, eventId, userId, isLoggedIn }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const data = await pluginApi.getPluginEventInstanceData();
        setNotes(data?.notes || []);
      } catch (error) {
        console.error('Error loading notes:', error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const deleteNote = async (noteId) => {
    const updatedNotes = notes.filter(note => note.id !== noteId);
    setNotes(updatedNotes);
    
    try {
      await pluginApi.savePluginEventInstanceData({ notes: updatedNotes });
    } catch (error) {
      console.error('Error deleting note:', error);
      // Revert on error
      setNotes(notes);
    }
  };

  const clearAllNotes = async () => {
    if (window.confirm('Are you sure you want to delete all notes?')) {
      setNotes([]);
      try {
        await pluginApi.savePluginEventInstanceData({ notes: [] });
      } catch (error) {
        console.error('Error clearing notes:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-md max-w-2xl mx-auto">
        <div className="text-center">Loading notes...</div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow-md max-w-2xl mx-auto">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">Guest Notes (Host View) 📝</h2>
        {notes.length > 0 && (
          <button
            onClick={clearAllNotes}
            className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition-colors"
          >
            Clear All
          </button>
        )}
      </div>

      <div className="mb-4 p-3 bg-yellow-50 border-l-4 border-yellow-400">
        <p className="text-sm text-yellow-800">
          <strong>Host View:</strong> You can see all guest notes and delete individual ones if needed.
        </p>
      </div>

      <div className="space-y-3">
        {notes.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No notes yet.</p>
        ) : (
          notes.map((note) => (
            <div key={note.id} className="p-3 bg-blue-50 rounded-lg border-l-4 border-blue-500 relative">
              <button
                onClick={() => deleteNote(note.id)}
                className="absolute top-2 right-2 text-red-500 hover:text-red-700 text-sm"
                title="Delete note"
              >
                ✕
              </button>
              <p className="text-gray-800 mb-2 pr-6">{note.text}</p>
              <div className="text-sm text-gray-600">
                <span className="font-medium">{note.author}</span> • {note.timestamp}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}`,
    [UserRole.HOST_CONFIG_VIEW]: `import React, { useState, useEffect } from 'react';

export default function HostConfigView({ pluginApi, eventId, userId, isLoggedIn }) {
  const [settings, setSettings] = useState({
    allowAnonymous: true,
    maxNoteLength: 500,
    moderationEnabled: false,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadConfig() {
      try {
        const config = await pluginApi.getPluginConfig();
        setSettings({
          allowAnonymous: config?.allowAnonymous ?? true,
          maxNoteLength: config?.maxNoteLength ?? 500,
          moderationEnabled: config?.moderationEnabled ?? false,
          ...config
        });
      } catch (error) {
        console.error('Error loading config:', error);
      } finally {
        setLoading(false);
      }
    }
    loadConfig();
  }, []);

  const saveSettings = async () => {
    try {
      await pluginApi.savePluginConfig(settings);
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Error saving settings');
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-md max-w-2xl mx-auto">
        <div className="text-center">Loading configuration...</div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow-md max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold mb-4">Guest Notes Configuration</h2>
      
      <div className="space-y-4">
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={settings.allowAnonymous}
              onChange={(e) => setSettings({...settings, allowAnonymous: e.target.checked})}
              className="mr-2"
            />
            Allow anonymous notes
          </label>
          <p className="text-sm text-gray-600 ml-6">Guests can leave notes without providing their name</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Maximum note length
          </label>
          <input
            type="number"
            value={settings.maxNoteLength}
            onChange={(e) => setSettings({...settings, maxNoteLength: parseInt(e.target.value)})}
            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            min="50"
            max="1000"
          />
        </div>

        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={settings.moderationEnabled}
              onChange={(e) => setSettings({...settings, moderationEnabled: e.target.checked})}
              className="mr-2"
            />
            Enable moderation
          </label>
          <p className="text-sm text-gray-600 ml-6">Notes require host approval before being visible</p>
        </div>
      </div>

      <button
        onClick={saveSettings}
        className="mt-6 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
      >
        Save Configuration
      </button>
    </div>
  );
}`,
  },
}
